<template>
  <CardLoaderSmall v-if="isLoading" class="col-span-full flex-1 flex-col" />
  <Box v-else>
    <template #title>Kontaktuppgifter</template>

    <Form @submit="saveChanges" ref="contactForm" v-slot="{ meta }">
      <!-- Phone sections -->
      <div class="space-y-6">
        <SectionHeader
          title="Kontaktuppgifter"
          :editable="canEdit"
          :editing="editable"
          :disabled="editDisabled"
          @edit="$emit('edit')"
          @cancel="cancelChanges"
        />
        <div class="space-y-2">
          <h3 class="text-b2 text-gray-900 font-bold uppercase tracking-wider font-neue-kabel text-[14px] font-thick">Adress</h3>

          <ul class="list-none space-y-1" v-if="!editable || hasContract || this.claims.indexOf('company') > -1">
            <li class="text-b1 text-gray-900">{{ profile.firstName }} {{ profile.lastName }}</li>
            <li class="text-b1 text-gray-900" v-if="profile.careOf">c/o {{ profile.careOf }}</li>
            <li class="text-b1 text-gray-900">{{ profile.postalAddress }}</li>
            <li class="text-b1 text-gray-900" v-if="profile.postalAddress2">{{ profile.postalAddress2 }}</li>
            <li class="text-b1 text-gray-900">{{ profile.zipCode }} {{ profile.city }}</li>
          </ul>
         <fieldset v-else class="flex flex-col items-start md:flex-col">
            <div class="flex w-full flex-col space-y-2 md:w-1/2" :class="{'!mb-4': editable }">
              <Input
                v-if="editable"
                :id="'address'"
                :label="'Adress *'"
                v-model="editableData.postalAddress"
                :rule-type="'required|max:30'"
                type="text"
                :placeholder="'Ange din adress'"
                :floatingLabel="true"
                :disabled="!editable"
              />
            </div>
           <div class="flex w-full flex-col space-y-2 md:w-1/2" :class="{'!mb-4': editable }">
             <Input
               v-if="editable"
               :id="'postalAddress2'"
               :label="'Adressrad två '"
               v-model="editableData.postalAddress2"
               :rule-type="'max:30'"
               type="text"
               placeholder='Adressrad två'
               :floatingLabel="true"
               :disabled="!editable"
             />
           </div>
            <Button
              v-if="!editableData.careOf && editable && !isAddingCoAddress"
              variant="text"
              class="!mt-1"
              @click="addCoAddress"
            >
              <template #icon-before>
                <IconPlus />
              </template>
              Lägg till en c/o adress
            </Button>
            <div class="flex w-full flex-col space-y-2 md:w-1/2 mb-4">
              <Input
                v-if="editable && (isAddingCoAddress || !!editableData.careOf)"
                :id="'coAddress'"
                :label="'C/o'"
                v-model="editableData.careOf"
                type="text"
                :placeholder="'Ange din c/o adress'"
                :floatingLabel="true"
                :disabled="!editable"
              />
            </div>
            <div class="flex w-full flex-col space-y-2 md:w-1/2 mb-4">
              <Input
                v-if="editable"
                :id="'zipCode'"
                :label="'Postkod *'"
                v-model="zipCode"
                :rule-type="'numeric|min:5|max:5'"
                type="text"
                :placeholder="'Ange din postkod'"
                :floatingLabel="true"
                :disabled="!editable"
              />
              <span v-if="fetchZipInfo">{{fetchZipInfo}}</span>
            </div>
            <div class="flex w-full flex-col space-y-2 md:w-1/2">
              <Input
                v-if="editable"
                :id="'city'"
                :label="'Ort (hämtas automatiskt) *'"
                v-model="editableData.city"
                :rule-type="'required'"
                type="tel"
                :placeholder="''"
                :floatingLabel="true"
                :disabled="true"
              />
            </div>
          </fieldset>
        </div>

        <div class="flex flex-col space-y-4">
          <h3 v-if="!editable" class="text-b2 mb-2 text-gray-900 font-bold uppercase tracking-wider font-neue-kabel text-[14px] font-thick">Telefonnummer</h3>
          <div v-for="(phoneType, key) in visiblePhoneTypes" :key="key">
            <div class="flex flex-col items-start md:flex-row md:space-x-5">
              <div class="flex w-full flex-col space-y-2 md:w-1/2">
                <Input
                  v-if="editable"
                  :id="key"
                  :label="phoneType.label"
                  v-model="editableData[key]"
                  type="tel"
                  :placeholder="phoneType.label"
                  :floatingLabel="true"
                  :disabled="!editable"
                  @keyup.delete="handleDefaultNumberChange($event, key)"
                />
                <p v-else>{{editableData[key]}}</p>
                <RadioButton
                  v-if="editable"
                  :id="'radio' + key"
                  name="defaultPhone"
                  v-model="editableData.defaultPhone"
                  :value="key"
                  label="Förvald"
                  :disabled="!editable"
                />
              </div>
              <Button
                v-if="canRemovePhone(key)"
                variant="danger"
                @click="removePhone(key)"
                class="ml-auto md:ml-0"
              >
                Ta bort
                <template #icon-after><IconTrashCan /></template>
              </Button>
            </div>
          </div>
        </div>

        <!-- Add phone button -->
        <Button
          v-if="availablePhoneTypes.length > 0 && editable"
          variant="text"
          class="!mt-3"
          @click="addPhone"
        >
          <template #icon-before>
            <IconPlus />
          </template>
          Lägg till telefonnummer
        </Button>

        <!-- Email -->
        <div class="flex flex-col space-y-2 md:w-1/2">
          <Input
            v-if="editable"
            id="email"
            label="E-postadress"
            v-model="editableData.email"
            :rule-type="'required|email'"
            type="email"
            placeholder="Ange din e-postadress"
            :floatingLabel="true"
          />
          <template v-else>
            <h3 class="text-b2 text-gray-900 font-bold uppercase tracking-wider font-neue-kabel text-[14px] font-thick">E-postadress</h3>
            <p class="text-[18px]">{{ profile.email }}</p>
          </template>
        </div>

        <div v-if="editable && emailChanged" class="w-fit border-0 border-l-4 border-solid border-turquoise-60 bg-turquoise-10 py-2 px-4 !mt-4">
          <p class="mb-2">Innan din ändring av e-postadress är slutförd finns det några saker du måste göra.</p>
          <ul class="mb-2 list-disc pl-6">
            <li>När du klickar på Spara ändringar loggas du ut.</li>
            <li>Därefter ska du logga in med din nya e-postadress.</li>
            <li>Du får då till den nya e-postadressen ett meddelande med en sexsiffrig kod du ska ange för att bekräfta den.</li>
          </ul>
          <p>Först när du bekräftat din nya e-postadress är du klar och din adress är ändrad i vårt system.</p>
        </div>

        <div class="input-field" v-if="profile.preferredContactMethod == 'letter'">
          <label class="form-label">Föredraget kontaktsätt</label>

          <RadioButton
            :id="PREFERED_CONTACT_METHOD_VALUES.EMAIL"
            label="E-post"
            name="preferredContactMethod"
            v-model="preferredContactMethod"
            :value="PREFERED_CONTACT_METHOD_VALUES.EMAIL"
          />
          <RadioButton
            :id="PREFERED_CONTACT_METHOD_VALUES.LETTER"
            label="Brev (utgått)"
            :disabled=true
            name="preferredContactMethod"
            v-model="preferredContactMethod"
            :value="PREFERED_CONTACT_METHOD_VALUES.LETTER"
          />


        </div>

        <div class="mb-4 w-fit rounded-[4px] bg-primary-10 pb-2 pl-4 pr-4 pt-2 !mt-4">
          <p>När vi behöver komma i kontakt med dig hör vi av oss via e-post.</p>
        </div>

        <!-- Save button -->
        <Button v-if="editable" type="submit" variant="primary" :loading="isSaving" loadingText="Sparar" :disabled="!meta.valid || !hasAtLeastOnePhone || !saveError && (!hasChanges || !editable) && !fetchZipInfo" size="large">
          Spara ändringar
        </Button>

        <p class="mb-4 w-fit rounded-[4px] bg-danger-10 pb-2 pl-4 pr-4 pt-2" v-if="editable && !hasAtLeastOnePhone">Du måste ange minst ett telefonnummer.</p>
        <p class="mb-4 w-fit rounded-[4px] bg-danger-10 pb-2 pl-4 pr-4 pt-2" v-if="editable && saveError">{{saveError}}</p>
      </div>
    </Form>
  </Box>
</template>

<script>
import Box from '@/components/ui/box';
import Input from '@/components/ui/form/input';
import RadioButton from '@/components/ui/form/radio';
import Button from '@/components/ui/button';
import IconPlus from '@/components/icons/IconPlus';
import IconTrashCan from '@/components/icons/IconTrashCan';
import CardLoaderSmall from '@/components/loader/CardLoaderSmall';
import SectionHeader from '@/components/profile/section-header';
import { Form } from 'vee-validate';
import { PREFERED_CONTACT_METHOD_VALUES } from '@/config/constants'
import { mapGetters } from 'vuex';

export default {
  name: 'ContactSection',
  components: {
    Box,
    Input,
    RadioButton,
    Button,
    SectionHeader,
    IconPlus,
    IconTrashCan,
    CardLoaderSmall,
    Form,
  },
  props: {
    profile: {
      type: Object,
      required: true,
    },
    // Om sektionen är i redigeringsläge
    editable: {
      type: Boolean,
      default: true,
    },
    // Om användaren får redigera (visar "Ändra")
    canEdit: {
      type: Boolean,
      default: true,
    },
    // En annan sektion redigeras
    editDisabled: {
      type: Boolean,
      default: false,
    },
    saveError: {
      type: String,
      required: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    hasContract: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['edit', 'cancel', 'save'],
  data() {
    return {
      PREFERED_CONTACT_METHOD_VALUES,
      isLoading: true,
      preferredContactMethod: "email",
      editableData: {},
      zipCode: this.profile.zipCode || '',
      fetchZipInfo: '',
      phoneTypes: {
        phoneMobile: {
          label: 'Telefon mobil',
          selected: false,
        },
        phoneHome: {
          label: 'Telefon hem',
          selected: true,
        },
        phoneMobile2: {
          label: 'Telefon',
          selected: false,
        },
        phoneMobile3: {
          label: 'Telefon',
          selected: false,
        },
        phoneWork: {
          label: 'Telefon arbete',
          selected: false,
        },
      },
      hasLoadedZip: false,
      isAddingCoAddress: false,
      // testAlwaysLoading: true,
    };
  },
  computed: {
    ...mapGetters(['claims']),
    visiblePhoneTypes() {
      return Object.keys(this.phoneTypes)
        .filter((key) => this.phoneTypes[key].selected)
        .reduce((obj, key) => {
          obj[key] = this.phoneTypes[key];
          return obj;
        }, {});
    },
    availablePhoneTypes() {
      return Object.keys(this.phoneTypes).filter((type) => !this.phoneTypes[type].selected);
    },
    hasChanges() {
      if (!this.profile) return false;

      return (
        this.editableData.phoneMobile !== this.profile.phoneMobile ||
        this.editableData.phoneHome !== this.profile.phoneHome ||
        this.editableData.phoneMobile2 !== this.profile.phoneMobile2 ||
        this.editableData.phoneMobile3 !== this.profile.phoneMobile3 ||
        this.editableData.phoneWork !== this.profile.phoneWork ||
        this.editableData.defaultPhone !== this.profile.defaultPhone ||
        this.editableData.postalAddress !== this.profile.postalAddress ||
        this.editableData.postalAddress2 !== this.profile.postalAddress2 ||
        this.editableData.careOf !== this.profile.careOf ||
        this.editableData.zipCode !== this.profile.zipCode ||
        this.profile.preferredContactMethod !== 'email' ||
        this.editableData.city !== this.profile.city ||
        this.emailChanged
      );
    },
    emailChanged() {
      return (this.editableData.email || '').trim() !== (this.profile.email || '').trim();
    },
    activePhoneSections() {
      return Object.keys(this.phoneTypes).filter((type) => this.phoneTypes[type].selected).length;
    },
    hasAtLeastOnePhone() {
      return !!(this.editableData.phoneMobile || this.editableData.phoneHome ||
                this.editableData.phoneMobile2 || this.editableData.phoneMobile3 ||
                this.editableData.phoneWork);
    },
  },
  watch: {
    "profile.zipCode": {
      immediate: true,
      handler(newZipCode) {
        this.zipCode = newZipCode || '';
        this.hasLoadedZip = true;
      }
    },

    profile: {
      immediate: true,
      deep: true,
      handler(profile) {
        if (profile && !this.testAlwaysLoading) {
          new Promise((resolve) => setTimeout(resolve, 500)).then(() => {
            this.resetFromProfile();

            if (Object.keys(profile).length != 0) this.isLoading = false;
          });
        }
      },
    },
    zipCode: {
      handler(newZipCode, oldZipCode) {
        if(newZipCode === this.profile.zipCode) return;
        // Gör inget om man inte fyllt i femsiffrigt
        if (!newZipCode || String(newZipCode).match(/^\d{5}$/) === null) {
          this.fetchZipInfo = '';
          this.zipCode = this.profile.zipCode || '';
          return;
        }
        this.fetchZipInfo = 'Hämtar ort...';
        return this.$store.cache
          .dispatch('checkZipcode', newZipCode)
          .then((result) => {
            this.editableData.zipCode = newZipCode;
            this.editableData.city =
              result.city.charAt(0).toUpperCase() + result.city.toLowerCase().substr(1);
          })
          .catch((err) => {
            this.editableData.city = '';
          })
          .finally(() => {
            this.fetchZipInfo = '';
          });
      },
    },
  },
  methods: {
    handleDefaultNumberChange(event, phoneType) {
      if (event.target.value === '' && phoneType === this.editableData.defaultPhone) {
        // Find another non-empty phone to be default
        if (this.editableData.phoneMobile) {
          this.editableData.defaultPhone = 'phoneMobile';
        } else if (this.editableData.phoneHome) {
          this.editableData.defaultPhone = 'phoneHome';
        } else if (this.editableData.phoneMobile2) {
          this.editableData.defaultPhone = 'phoneMobile2';
        } else if (this.editableData.phoneMobile3) {
          this.editableData.defaultPhone = 'phoneMobile3';
        } else if (this.editableData.phoneWork) {
          this.editableData.defaultPhone = 'phoneWork';
        } else {
          this.editableData.defaultPhone = '';
        }
      }
    },

    canRemovePhone(phoneType) {
      if (!this.editable) return false;
      // Never allow removing the default phone
      if (phoneType === 'phone') return false;

      // Keep at least one phone section
      return this.activePhoneSections > 1;
    },

    removePhone(phoneType) {
      if (this.activePhoneSections <= 1) return;

      // Clear phone value
      this.editableData[phoneType] = '';

      // Hide this phone section
      this.phoneTypes[phoneType].selected = false;

      // If this was default, change default to another phone
      if (this.editableData.defaultPhone === phoneType) {
        const activePhoneType = Object.keys(this.phoneTypes).find(
          (type) => this.phoneTypes[type].selected && type !== phoneType,
        );

        if (activePhoneType) {
          this.editableData.defaultPhone = activePhoneType;
        }
      }
    },

    addPhone() {
      if (this.availablePhoneTypes.length === 0) return;

      // Add first available phone type
      const firstAvailable = this.availablePhoneTypes[0];
      if (firstAvailable) {
        this.phoneTypes[firstAvailable].selected = true;

        // Initialize if empty
        if (!this.editableData[firstAvailable]) {
          this.editableData[firstAvailable] = '';
        }
      }
    },
    addCoAddress() {
      this.isAddingCoAddress = true;
      this.editableData.careOf = '';
      this.$nextTick(() => {
        const input = document.getElementById('coAddress');
        if (input) {
          input.focus();
        }
      });
    },

    saveChanges() {
      this.editableData.preferredContactMethod = 'email';
      this.editableData.email = (this.editableData.email || '').trim();
      this.$emit('save', this.editableData);
    },

    resetFromProfile() {
      this.editableData = { ...this.profile };
      this.zipCode = this.profile.zipCode || '';
      this.isAddingCoAddress = false;

      // Set phone visibility based on profile data
      Object.keys(this.phoneTypes).forEach((type) => {
        this.phoneTypes[type].selected = !!this.profile[type];
      });

      // Ensure at least one phone is visible
      if (!Object.values(this.phoneTypes).some((type) => type.selected)) {
        this.phoneTypes['phoneMobile'].selected = true;
      }
    },

    cancelChanges() {
      this.resetFromProfile();
      this.$emit('cancel');
    },
  },
};
</script>
